import { tourStopsData, TourStop, getFeaturedEvent, getAllTourStops, getTourStopById } from '../data/tourStops';
import { tourStopsAPI, registrationsAPI } from './api';

// Interface pour les données d'inscription
export interface RegistrationData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  category: string;
  experience: string;
  terms: boolean;
  eventId?: number;
  submittedAt?: string;
}

// Interface pour la réponse d'inscription
export interface RegistrationResponse {
  success: boolean;
  confirmationNumber?: string;
  message: string;
  registrationId?: number;
}

// Simulation d'une base de données pour les inscriptions
let registrations: (RegistrationData & { id: number; confirmationNumber: string })[] = [];
let registrationIdCounter = 1;

// Fonction pour générer un numéro de confirmation
const generateConfirmationNumber = (): string => {
  const prefix = 'ASC';
  const timestamp = Date.now().toString().slice(-6);
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `${prefix}${timestamp}${random}`;
};

// API simulée pour les événements du tour
export const tourApi = {
  // Récupérer tous les événements
  async getTourStops(): Promise<TourStop[]> {
    // Simulation d'un délai réseau
    await new Promise(resolve => setTimeout(resolve, 300));
    
    try {
      // Appel à l'API PHP/PDO
      const response = await tourStopsAPI.getAll();
      
      // Transformer les données pour correspondre à l'interface TourStop
      const transformedData = response.records.map((stop: any) => ({
        ...stop,
        highlights_fr: typeof stop.highlights_fr === 'string' 
          ? stop.highlights_fr.split(',').map((h: string) => h.trim())
          : stop.highlights_fr || [],
        highlights_en: typeof stop.highlights_en === 'string'
          ? stop.highlights_en.split(',').map((h: string) => h.trim())
          : stop.highlights_en || []
      }));
      
      return transformedData;
    } catch (error) {
      console.error('Erreur lors de la récupération des événements:', error);
      
      // Pour l'instant, on retourne les mock data
      return getAllTourStops();
    }
  },

  // Récupérer l'événement à la une
  async getFeaturedEvent(): Promise<TourStop | null> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    try {
      // Appel à l'API PHP/PDO
      const response = await tourStopsAPI.getFeatured();
      
      if (response) {
        // Transformer les données
        return {
          ...response,
          highlights_fr: typeof response.highlights_fr === 'string' 
            ? response.highlights_fr.split(',').map((h: string) => h.trim())
            : response.highlights_fr || [],
          highlights_en: typeof response.highlights_en === 'string'
            ? response.highlights_en.split(',').map((h: string) => h.trim())
            : response.highlights_en || []
        };
      }
      
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'événement à la une:', error);
      
      return getFeaturedEvent();
    }
  },

  // Récupérer un événement par ID
  async getTourStopById(id: number): Promise<TourStop | null> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    try {
      // Appel à l'API PHP/PDO
      const response = await tourStopsAPI.getById(id);
      
      if (response) {
        // Transformer les données
        return {
          ...response,
          highlights_fr: typeof response.highlights_fr === 'string' 
            ? response.highlights_fr.split(',').map((h: string) => h.trim())
            : response.highlights_fr || [],
          highlights_en: typeof response.highlights_en === 'string'
            ? response.highlights_en.split(',').map((h: string) => h.trim())
            : response.highlights_en || []
        };
      }
      
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'événement:', error);
      
      return getTourStopById(id);
    }
  }
};

// API simulée pour les inscriptions
export const registrationApi = {
  // Soumettre une inscription
  async submitRegistration(registrationData: RegistrationData): Promise<RegistrationResponse> {
    // Simulation d'un délai réseau
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    try {
      // Validation basique
      if (!registrationData.firstName || !registrationData.lastName || !registrationData.email) {
        return {
          success: false,
          message: 'Veuillez remplir tous les champs obligatoires.'
        };
      }

      if (!registrationData.terms) {
        return {
          success: false,
          message: 'Vous devez accepter les conditions générales.'
        };
      }

      // Appel à l'API PHP/PDO
      const response = await registrationsAPI.create({
        ...registrationData,
        submitted_at: new Date().toISOString()
      });
      
      return response;

    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      
      // Fallback vers simulation en cas d'erreur
      const confirmationNumber = generateConfirmationNumber();
      const newRegistration = {
        ...registrationData,
        id: registrationIdCounter++,
        confirmationNumber,
        submittedAt: new Date().toISOString()
      };

      registrations.push(newRegistration);

      return {
        success: true,
        confirmationNumber,
        registrationId: newRegistration.id,
        message: 'Inscription enregistrée avec succès ! Vous recevrez une confirmation par email.'
      };
    }
  },

  // Récupérer toutes les inscriptions (pour l'admin)
  async getRegistrations(): Promise<(RegistrationData & { id: number; confirmationNumber: string })[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    try {
      // Appel à l'API PHP/PDO
      const response = await registrationsAPI.getAll();
      return response.records;
    } catch (error) {
      console.error('Erreur lors de la récupération des inscriptions:', error);
      
      return registrations;
    }
  },

  // Vérifier une inscription par numéro de confirmation
  async getRegistrationByConfirmation(confirmationNumber: string): Promise<(RegistrationData & { id: number; confirmationNumber: string }) | null> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    try {
      // Appel à l'API PHP/PDO
      const response = await registrationsAPI.getByConfirmation(confirmationNumber);
      return response;
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'inscription:', error);
      
      return registrations.find(reg => reg.confirmationNumber === confirmationNumber) || null;
    }
  }
};

// Export des APIs

export default tourApi